import React from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';
import ROUTES from '@CONSTANTS/ROUTES.constant';
import {
    BrandStep,
    DocumentStep,
    OptionsStep,
    TypeStep,
} from '@REDUCERS';

import { GenerationMode, generationModes } from '@PAGES/manager/options/Options.page';

import styles from './StepSidebar.module.scss';

const cx: CX = classnames.bind(styles);

const baseSteps = [
    GenerationSteps.DOCUMENT,
    GenerationSteps.TYPE,
    GenerationSteps.BRAND,
    GenerationSteps.LOCATION,
    GenerationSteps.OPTIONS,
];

const stepLabels: Record<string, string> = {
    [GenerationSteps.DOCUMENT]: 'Document',
    [GenerationSteps.TYPE]: 'Type',
    [GenerationSteps.BRAND]: 'Brand', 
    [GenerationSteps.LOCATION]: 'Location',
    [GenerationSteps.OPTIONS]: 'Options',
    [GenerationSteps.SELECT_CONTENT]: 'Select Content',
};

const stepRoutes: Record<GenerationSteps, string> = {
    [GenerationSteps.DOCUMENT]: ROUTES.chooseDocument.path,
    [GenerationSteps.TYPE]: ROUTES.type.path,
    [GenerationSteps.BRAND]: ROUTES.brand.path,
    [GenerationSteps.LOCATION]: ROUTES.location.path,
    [GenerationSteps.OPTIONS]: ROUTES.options.path,
    [GenerationSteps.SELECT_CONTENT]: ROUTES.selectContent.path,
    [GenerationSteps.DOWNLOAD_DOCUMENT]: ROUTES.downloadDocument.path,
};

function StepSidebar() {
    const {
        services,
    } = useDocumentGenerationSteps();

    const location = useLocation();
    const navigate = useNavigate();

    const documentStep: DocumentStep = services.getStepData(GenerationSteps.DOCUMENT);
    const typeStep: TypeStep = services.getStepData(GenerationSteps.TYPE);
    const brandStep: BrandStep = services.getStepData(GenerationSteps.BRAND);
    const locationStep: any = services.getStepData(GenerationSteps.LOCATION);
    const optionsStep: OptionsStep = services.getStepData(GenerationSteps.OPTIONS);

    // Determine if step 6 (Select Content) should be shown based on partial extraction mode
    const shouldShowSelectContent = optionsStep?.data?.id === GenerationMode.PARTIAL;
    const steps = shouldShowSelectContent 
        ? [...baseSteps, GenerationSteps.SELECT_CONTENT]
        : baseSteps;

    const getStepState = (step: GenerationSteps) => {
        const activeStepIndex = steps.indexOf(location.pathname as GenerationSteps);
        const currentStepIndex = steps.indexOf(step);

        return {
            isActive: activeStepIndex === currentStepIndex,
            isCompleted: activeStepIndex > currentStepIndex,
            isUpcoming: activeStepIndex < currentStepIndex,
        };
    };

    const getStepValue = (step: GenerationSteps) => {
        switch (step) {
            case GenerationSteps.DOCUMENT:
                return documentStep.data?.name || '';
            case GenerationSteps.TYPE:
                return typeStep.data?.name || '';
            case GenerationSteps.BRAND:
                return brandStep.data?.name || '';
            case GenerationSteps.LOCATION:
                return locationStep.data?.name || '';
            case GenerationSteps.OPTIONS:
                if (optionsStep.isComplete) {
                    const mode = generationModes.find((m) => m.id === optionsStep.data.id)!.name;
                    return optionsStep.data.propertyName ? `${mode} • ${optionsStep.data.propertyName}` : mode;
                }
                return '';
            case GenerationSteps.SELECT_CONTENT:
                return 'Content Selection';
            default:
                return '';
        }
    };

    const handleStepClick = (step: GenerationSteps, state: { isCompleted: boolean; isActive: boolean }) => {
        // Only allow navigation to completed steps (previous steps)
        if (state.isCompleted && stepRoutes[step]) {
            navigate(stepRoutes[step]);
        }
    };

    return (
        <div className={cx('modern-step-sidebar')}>
            <div className={cx('sidebar-header')}>
                <h3 className={cx('sidebar-title')}>Generation Progress</h3>
                <div className={cx('progress-indicator')}>
                    <span className={cx('progress-text')}>
                        {steps.indexOf(location.pathname as GenerationSteps) + 1} of {steps.length}
                    </span>
                </div>
            </div>

            <div className={cx('steps-container')}>
                {steps.map((step: GenerationSteps, index: number) => {
                    const state = getStepState(step);
                    const value = getStepValue(step);
                    const isLast = index === steps.length - 1;
                    const isClickable = state.isCompleted;

                    const stepProps = isClickable ? {
                        role: 'button',
                        tabIndex: 0,
                        onClick: () => handleStepClick(step, state),
                        onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleStepClick(step, state);
                            }
                        },
                        'aria-label': `Go back to ${stepLabels[step] || step.replace('/', '').charAt(0).toUpperCase() + step.replace('/', '').slice(1)} step`,
                    } : {};

                    return (
                        <div 
                            key={step} 
                            className={cx('step-item', {
                                active: state.isActive,
                                completed: state.isCompleted,
                                upcoming: state.isUpcoming,
                                clickable: isClickable,
                            })}
                            {...stepProps}
                        >
                            <div className={cx('step-indicator')}>
                                <div className={cx('step-number')}>
                                    {state.isCompleted ? (
                                        <svg className={cx('check-icon')} viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <span className={cx('number')}>{index + 1}</span>
                                    )}
                                </div>
                                {!isLast && <div className={cx('step-connector')} />}
                            </div>

                            <div className={cx('step-content')}>
                                <div className={cx('step-label')}>
                                    {stepLabels[step] || step.replace('/', '').charAt(0).toUpperCase() + step.replace('/', '').slice(1)}
                                </div>
                                {value && (
                                    <div className={cx('step-value')}>
                                        {value}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default React.memo(StepSidebar, () => true);
